<script setup>
import { onBeforeUnmount, onBeforeMount, computed } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import cookie from '../assets/js/cookies.js';
import loadGif from "@/assets/img/loading.gif";
import router from "../router/index.js";

import logo from "@/assets/img/1.png";
import logoWhite from "@/assets/img/logo-financas-branco-transparente.png";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const body = document.getElementsByTagName("body")[0];
const store = useStore();
const darkMode = computed(() => store.state.darkMode);

onBeforeMount(() => {
  let session = cookie.get('user_agive_session');

  if (session != null) {
    router.push({ name: 'Visão Geral' });
  }

  store.state.hideConfigButton = true;
  store.state.showNavbar = false;
  store.state.showSidenav = false;
  store.state.showFooter = false;
  body.classList.remove("bg-gray-100");

  store.state.email = {
    hasError: false,
    messageError: '',
    value: '',
  };

  store.state.password = {
    hasError: false,
    messageError: '',
    value: '',
  };

  store.state.remember = {
    checked: false,
  };

  store.state.login = {
    hasError: false,
    messageError: ''
  };

  store.state.isLoging = false;
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  store.state.showNavbar = true;
  store.state.showSidenav = true;
  store.state.showFooter = true;
  body.classList.add("bg-default");
});

const login = () => {
  store.state.isLoging = true;

  let data = new FormData();

  store.state.email.hasError = false;
  store.state.password.hasError = false;
  store.state.login.hasError = false;

  let device = getDeviceName();

  data.append('email', store.state.email.value);
  data.append('password', store.state.password.value);
  data.append('remember', store.state.remember.checked);
  data.append('device', device);

  axios.post('/auth', data)
    .then(res => {
      if (res.data.error) {
        let messages = res.data.messages;

        Object.entries(messages).forEach((value) => {
          if (value[0] == 'email') {
            store.state.email.hasError = true;
            store.state.email.messageError = value[1];
          }
          else if (value[0] == 'password') {
            store.state.password.hasError = true;
            store.state.password.messageError = value[1];
          }
          else if (value[0] == 'error') {
            store.state.login.hasError = true;
            store.state.login.messageError = value[1];
          }
        })

        store.state.isLoging = false;
      }
      else {
        let expires = 86400;

        if (store.state.remember.checked) {
          expires = 31622400;
        }

        const emailValue = store.state.email.value;

        store.state.email = {
          hasError: false,
          messageError: '',
          value: '',
        };

        store.state.password = {
          hasError: false,
          messageError: '',
          value: '',
        };

        store.state.remember = {
          checked: false,
        };

        cookie.add('user_agive_session', res.data, expires);
        cookie.add('user_agive_email', emailValue, expires);

        window.location.href = '/inicio';
      }
    })
    .catch(err => {
      console.error(err);
      store.state.isLoging = false;
    })
}

const iosDeviceMapping = new Map([
  ["320x480", "IPhone 4S, 4, 3GS, 3G, 1st gen"],
  ["320x568", "IPhone 5, SE 1st Gen,5C, 5S"],
  ["375x667", "IPhone SE 2nd Gen, 6, 6S, 7, 8"],
  ["375x812", "IPhone X, XS, 11 Pro, 12 Mini, 13 Mini"],
  ["390x844", "IPhone 13, 13 Pro, 12, 12 Pro"],
  ["414x736", "IPhone 8+"],
  ["414x896", "IPhone 11, XR, XS Max, 11 Pro Max"],
  ["428x926", "IPhone 13 Pro Max, 12 Pro Max"],
  ["476x847", "IPhone 7+, 6+, 6S+"],
  ["744x1133", "IPad Mini 6th Gen"],
  ["768x1024", "IPad Mini (5th Gen), IPad (1-6th Gen), iPad Pro (1st Gen 9.7), Ipad Mini (1-4), IPad Air(1-2)"],
  ["810x1080", "IPad 7-9th Gen"],
  ["820x1180", "iPad Air (4th gen)"],
  ["834x1194", "iPad Pro (3-5th Gen 11)"],
  ["834x1112", "iPad Air (3rd gen), iPad Pro (2nd gen 10.5)"],
  ["1024x1366", "iPad Pro (1-5th Gen 12.9)"],
]);

const desktopDeviceMapping = new Map([
  ["Win32", "Windows"],
  ["Linux", "Linux"],
  ["MacIntel", "Mac OS"],
  ["Windows", "Windows"]
]);

const getAndroidDeviceName = () => {
  const androidUserAgentString = window.navigator.userAgent.slice(window.navigator.userAgent.indexOf("Android"));
  const androidDeviceName = androidUserAgentString.slice(androidUserAgentString.indexOf("; ") + 1, androidUserAgentString.indexOf(")"));
  if (androidDeviceName) {
    return androidDeviceName.trim().split(" ")[0];
  }

  return "Android";
};

const getIosDeviceName = () => {
  const screenResolution = window.screen.width + 'x' + window.screen.height;
  const device = iosDeviceMapping.get(screenResolution);
  if (device) {
    return device;
  }
  return "Iphone";
};

const getDesktopDeviceName = () => {
  const platform = navigator?.userAgentData?.platform || navigator?.platform || "unknown";
  let device = desktopDeviceMapping.get(platform) ?? "Unknown";
  return device;
};

const getDeviceName = () => {
  let device = "";
  const isMobileDevice = window.navigator.userAgent.toLowerCase().includes("mobi");

  if (isMobileDevice) {
    if (window.navigator.userAgent.includes("Android")) {
      device = getAndroidDeviceName();
    } else {
      device = getIosDeviceName();
    }
  } else {
    device = getDesktopDeviceName();
  }

  return device;
}
</script>

<template>
  <main
    class="signin-page main-content"
    :class="{ 'bg-azul-petroleo': !darkMode, 'signin-page-dark': darkMode }"
  >
    <section class="signin-section">
      <div class="container py-4 py-lg-0 h-100">
        <div class="row min-vh-100 align-items-center justify-content-center">
          <div class="col-12 col-lg-10 col-xl-9">
            <div class="signin-shell" :class="{ 'signin-shell-dark': darkMode }">
              <div class="row g-0 align-items-stretch">
                <div class="col-lg-5 d-none d-lg-flex">
                  <div class="signin-brand-panel">
                    <div class="signin-brand-panel__content">
                      <span class="signin-brand-badge">Contive</span>
                      <h1 class="signin-brand-title">Controle financeiro com visual moderno e simples.</h1>
                      <p class="signin-brand-text">
                        Organize receitas, despesas e acompanhe seus resultados em um ambiente limpo e confiável.
                      </p>

                      <div class="signin-brand-highlights">
                        <div class="signin-highlight-item">
                          <strong>Gestão intuitiva</strong>
                          <span>Lançamentos, contas e relatórios no mesmo padrão visual do sistema.</span>
                        </div>
                        <div class="signin-highlight-item">
                          <strong>Acesso rápido</strong>
                          <span>Entre na sua conta e continue de onde parou.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-lg-7">
                  <div class="signin-card" :class="{ 'bg-white': !darkMode, 'signin-card-dark': darkMode }">
                    <div class="signin-card-header text-center text-lg-start">
                      <img :src="!darkMode ? logo : logoWhite" alt="Contive" class="logo-sidebar-login">

                      <div class="signin-header-copy">
                        <h2 class="signin-title" :class="{ 'fc-azul-petroleo': !darkMode, 'text-white': darkMode }">
                          {{ t('message.login.access_account') }}
                        </h2>
                        <p class="signin-subtitle" :class="{ 'text-muted': !darkMode, 'text-white opacity-8': darkMode }">
                          {{ t('message.login.access_account_subtitle') }}
                        </p>
                      </div>
                    </div>

                    <div class="signin-card-body">
                      <div class="mb-3">
                        <label class="signin-label" for="email">{{ t('message.global.email') }}</label>
                        <input
                          id="email"
                          v-model="store.state.email.value"
                          type="email"
                          class="form-control signin-input"
                          :placeholder="t('message.global.email')"
                          :class="{
                            darkmode: darkMode,
                            'is-invalid': store.state.email.hasError
                          }"
                        >
                        <div id="emailFeedback" class="invalid-feedback">
                          {{ store.state.email.messageError }}
                        </div>
                      </div>

                      <div class="mb-3">
                        <div class="d-flex justify-content-between align-items-center gap-3 mb-2">
                          <label class="signin-label mb-0" for="password">{{ t('message.global.password') }}</label>
                          <router-link class="signin-inline-link" :to="'/recuperar-senha'">
                            Esqueci minha senha
                          </router-link>
                        </div>
                        <input
                          id="password"
                          v-model="store.state.password.value"
                          type="password"
                          class="form-control signin-input"
                          :placeholder="t('message.global.password')"
                          :class="{
                            darkmode: darkMode,
                            'is-invalid': store.state.password.hasError
                          }"
                          @keyup.enter="login()"
                        >
                        <div id="passwordFeedback" class="invalid-feedback">
                          {{ store.state.password.messageError }}
                        </div>
                      </div>

                      <div class="signin-options-row mb-4">
                        <div class="form-check signin-check-wrap mb-0">
                          <input
                            id="termsCheck"
                            v-model="store.state.remember.checked"
                            class="form-check-input signin-check"
                            type="checkbox"
                          >
                          <label class="form-check-label signin-check-label" for="termsCheck">
                            {{ t('message.login.reminder_me') }}
                          </label>
                        </div>
                      </div>

                      <div class="text-center">
                        <button
                          v-if="!store.state.isLoging"
                          class="btn signin-submit w-100"
                          @click="login()"
                        >
                          {{ t('message.login.login') }}
                        </button>
                        <div v-else class="signin-loading-wrap">
                          <img :src="loadGif" alt="Carregando" width="50">
                        </div>
                      </div>

                      <div v-if="store.state.login.hasError" class="col-12 mt-3">
                        <div class="signin-error text-center">
                          {{ store.state.login.messageError }}
                        </div>
                      </div>

                      <div class="signin-divider"></div>

                      <div class="signin-signup text-center">
                        <span class="signin-signup-text">{{ t('message.login.no_account') }}</span>
                        <router-link :to="'/cadastro'" class="signin-signup-link">
                          {{ t('message.login.do_singup') }}
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.bg-4a838b {
  background-color: #0c2b3a;
}

.bg-azul-petroleo {
  background:
    radial-gradient(circle at top left, rgba(209, 133, 54, 0.12), transparent 28%),
    radial-gradient(circle at bottom right, rgba(255, 255, 255, 0.06), transparent 22%),
    #0c2b3a !important;
}

.fc-azul-petroleo {
  color: #0c2b3a !important;
}

.bg-mostarda {
  background-color: #d18536 !important;
}

.bg-fbfbf3 {
  background-color: #fbfbf3 !important;
}

.bg-266563 {
  background-color: #266563 !important;
}

.bg-041b23 {
  background-color: #041b23 !important;
}

.signin-page {
  min-height: 100vh;
}

.signin-section {
  min-height: 100vh;
}

.signin-shell {
  background: rgba(251, 251, 243, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(10px);
}

.signin-shell-dark {
  background: rgba(15, 19, 24, 0.78);
}

.signin-brand-panel {
  height: 100%;
  padding: 2.5rem 2.25rem;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01)),
    linear-gradient(135deg, #0c2b3a 0%, #12384a 62%, #1a4658 100%);
  color: #fff;
  display: flex;
  align-items: center;
}

.signin-brand-panel__content {
  width: 100%;
}

.signin-brand-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
}

.signin-brand-title {
  font-size: 2rem;
  line-height: 1.18;
  font-weight: 800;
  color: #fff;
  margin-bottom: 1rem;
}

.signin-brand-text {
  font-size: 1rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
}

.signin-brand-highlights {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.signin-highlight-item {
  padding: 1rem 1.1rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
}

.signin-highlight-item strong {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.98rem;
}

.signin-highlight-item span {
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.92rem;
  line-height: 1.55;
}

.signin-card {
  height: 100%;
  padding: 2.5rem 2rem;
  border: 0;
}

.signin-card-dark {
  background: #10161d !important;
}

.signin-card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0;
  border: 0;
  background: transparent !important;
  margin-bottom: 2rem;
}

.logo-sidebar-login {
  width: min(220px, 72%);
  height: auto;
  display: block;
}

.signin-header-copy {
  width: 100%;
}

.signin-title {
  margin-bottom: 0.45rem;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.signin-subtitle {
  margin-bottom: 0;
  font-size: 1rem;
  line-height: 1.6;
}

.signin-card-body {
  padding: 0;
}

.signin-label {
  display: inline-block;
  margin-bottom: 0.65rem;
  color: #284255;
  font-size: 0.92rem;
  font-weight: 700;
}

.signin-input {
  min-height: 54px;
  padding: 0.95rem 1rem;
  border-radius: 14px;
  border: 1px solid #d8e0e5;
  background: #ffffff;
  color: #0c2b3a;
  font-size: 0.98rem;
  box-shadow: none;
  transition: all 0.2s ease;
}

.signin-input::placeholder {
  color: #9ca9b5;
}

.form-control:focus,
.form-select:focus {
  border-color: #d18536 !important;
  box-shadow: 0 0 0 0.18rem rgba(209, 133, 54, 0.14) !important;
}

.form-control.darkmode,
.form-select.darkmode {
  background: #111922;
  border-color: #243645;
  color: #f5f7fa;
}

.form-control.darkmode::placeholder {
  color: #7e90a0;
}

.form-control.darkmode:focus,
.form-select.darkmode:focus {
  border-color: #d18536 !important;
}

.signin-options-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.signin-check-wrap {
  display: flex;
  align-items: center;
  min-height: 24px;
}

.signin-check {
  width: 1.1rem;
  height: 1.1rem;
  margin-top: 0.05rem !important;
  border: 1px solid #b9c8d3;
  box-shadow: none !important;
}

.form-check-input:checked[type=checkbox],
.form-check-input:checked[type=radio] {
  background-color: #d18536 !important;
  border-color: #d18536 !important;
  background-image: none !important;
}

.signin-check-label {
  color: #4f6475;
  font-size: 0.95rem;
  margin-left: 0.45rem;
}

.signin-inline-link,
.signin-signup-link {
  color: #d18536;
  font-weight: 700;
  text-decoration: none;
}

.signin-inline-link:hover,
a:hover,
.signin-signup-link:hover {
  color: #b66f26 !important;
}

.signin-submit {
  min-height: 56px;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #d18536 0%, #e09a4e 100%);
  color: #fff !important;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  box-shadow: 0 14px 30px rgba(209, 133, 54, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.signin-submit:hover {
  color: #fff !important;
  transform: translateY(-1px);
  box-shadow: 0 18px 34px rgba(209, 133, 54, 0.26);
}

.signin-loading-wrap {
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signin-error {
  color: #dc3545;
  font-size: 0.94rem;
  font-weight: 600;
}

.signin-divider {
  height: 1px;
  width: 100%;
  background: linear-gradient(90deg, transparent, rgba(12, 43, 58, 0.12), transparent);
  margin: 2rem 0 1.5rem;
}

.signin-signup {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.signin-signup-text {
  color: #698092;
}

.signin-page-dark .signin-label,
.signin-card-dark .signin-check-label,
.signin-page-dark .signin-signup-text {
  color: #c6d0d8;
}

.signin-page-dark .signin-divider {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent);
}

@media (min-width: 992px) {
  .signin-card {
    padding: 3rem;
  }

  .signin-card-header {
    align-items: flex-start;
    text-align: left !important;
  }
}

@media (max-width: 991.98px) {
  .signin-shell {
    border-radius: 24px;
  }

  .signin-card {
    padding: 2rem 1.35rem;
  }

  .signin-title {
    font-size: 1.8rem;
  }
}

@media (max-width: 575.98px) {
  .signin-shell {
    border-radius: 20px;
  }

  .signin-card {
    padding: 1.4rem 1rem;
  }

  .logo-sidebar-login {
    width: min(180px, 70%);
  }

  .signin-title {
    font-size: 1.6rem;
  }

  .signin-subtitle {
    font-size: 0.95rem;
  }

  .signin-input,
  .signin-submit {
    min-height: 52px;
  }
}
</style>
